import axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from "axios";

type RequestInterceptor = (
	config: InternalAxiosRequestConfig,
) => Promise<InternalAxiosRequestConfig>;
type ResponseInterceptor = <T extends AxiosResponse<unknown, unknown>>(
	data: T,
) => Promise<T>;

const createInterceptorStack =
	<K, T extends Array<(payload: K) => Promise<Awaited<K>>>>(interceptors: T) =>
	(config: K) =>
		interceptors.reduce(
			(promise, interceptor) => promise.then(interceptor),
			Promise.resolve(config),
		);

/**
 * Creates a configurable Axios-based HTTP client with support for both request and response interceptors.
 *
 * @template Config - Extends Axios's `InternalAxiosRequestConfig`, allowing consumers to define additional properties
 *                    used within the interceptors or request configs.
 *
 * @param {object} options - Configuration options for the client.
 * @param {string} [options.baseURL] - The base URL for all HTTP requests. Defaults to `undefined`.
 * @param {Record<string, string>} [options.headers] - Default headers applied to every request. Defaults to `{}`.
 * @param {(err: AxiosError) => void} [options.onError] - Optional error handler called when a request or response
 *                                                       interceptor fails. If omitted, Axios will throw errors normally.
 * @param {RequestInterceptor[]} [options.requestInterceptors] - An array of async functions (request interceptors).
 *                                                               Each function receives and returns an Axios request config.
 *                                                               Defaults to an empty array.
 * @param {ResponseInterceptor[]} [options.responseInterceptors] - An array of async functions (response interceptors).
 *                                                                 Each function receives and returns an Axios response.
 *                                                                 If none are provided, a default interceptor simply
 *                                                                 returns the original response payload.
 *
 * @returns {object} A client object containing shorthand HTTP methods: `get`, `post`, `patch`, `put`, and `delete`.
 *                   Each method returns a promise that resolves with the Axios response of the given type.
 *
 * @example
 * import createAxiosClient from "./createAxiosClient";
 * import { z } from "zod";
 *
 * // Example of a Zod schema you might use to parse responses
 * const UserSchema = z.object({
 *   id: z.number(),
 *   name: z.string(),
 * });
 *
 * // Create a client with a baseURL, custom headers, and interceptors
 * const client = createAxiosClient({
 *   baseURL: "https://api.example.com",
 *   headers: { Authorization: "Bearer token" },
 *   onError: (err) => {
 *     console.error("API error:", err.message);
 *   },
 *   requestInterceptors: [
 *     async (config) => {
 *       // e.g., attach additional headers or parameters
 *       config.headers["X-Custom-Header"] = "MyValue";
 *       return config;
 *     },
 *   ],
 *   responseInterceptors: [
 *     async (response) => {
 *       // e.g., transform the response data or validate it with a schema
 *       UserSchema.parse(response.data);
 *       return response;
 *     },
 *   ],
 * });
 *
 * // Use the client to fetch data
 * const fetchUser = async (id: number) => {
 *   const response = await client.get(`/users/${id}`);
 *   return response.data; // typed as unknown by default, or validated if using a schema
 * };
 */
const createAxiosClient = <
	Config extends InternalAxiosRequestConfig = InternalAxiosRequestConfig,
>({
	baseURL,
	headers,
	onError,
	requestInterceptors = [],
	responseInterceptors = [],
}: {
	baseURL?: string;
	headers?: Record<string, string>;
	onError?: (err: AxiosError) => void;
	requestInterceptors?: RequestInterceptor[];
	responseInterceptors?: ResponseInterceptor[];
}) => {
	const request = axios.create({ baseURL, headers });

	request.interceptors.request.use(
		createInterceptorStack(requestInterceptors),
		onError,
	);

	request.interceptors.response.use(
		createInterceptorStack([
			// If no response interceptors are provided then at least
			// one response interceptor must return the original response payload
			async (res) => res,
			...responseInterceptors,
		]),
		onError,
	);

	const makeHeaders = (
		args?: Partial<Config>,
		data?: AxiosRequestConfig["data"],
	): AxiosRequestConfig => {
		return {
			...args,
			...(data?.responseType ? { responseType: data.responseType } : {}),
			...(args?.headers ? { headers: args.headers } : {}),
		};
	};

	return {
		get: <T>(path: string, config?: Partial<Config>) =>
			request.get<T>(path, makeHeaders(config)),
		post: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			config?: Partial<Config>,
		) => request.post<T>(path, data, makeHeaders(config, data)),
		patch: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			config?: Partial<Config>,
		) => request.patch<T>(path, data, makeHeaders(config, data)),
		put: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			config?: Partial<Config>,
		) => request.put<T>(path, data, makeHeaders(config, data)),
		delete: <T>(path: string, config?: Partial<Config>) =>
			request.delete<T>(path, makeHeaders(config)),
	};
};

export default createAxiosClient;
